import React, { useLayoutEffect } from "react";
import PropTypes from "prop-types";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Responsive from "@amcharts/amcharts5/themes/Responsive";
import { format, getTime } from 'date-fns';
import { find, isObject, orderBy, groupBy } from "lodash";
import "./index.css";
import { getScrolling, getTimeZoneFromUtc } from "../utils"
import _this from '../constants'

const Gantt = ({ chartID, dataSet, timeZone, elementName }) => {

  useLayoutEffect(() => {

    // Get Viewport.
    let container = document.querySelector(`#${chartID}`);
    let viewport = container.getBoundingClientRect();

    // Get global constants.
    const { MIN_GRID_VALUE, MARGIN_CHART_CONTAINER } = _this;
    MARGIN_CHART_CONTAINER.paddingTop = 25;
    MARGIN_CHART_CONTAINER.paddingBottom = -2;
    MARGIN_CHART_CONTAINER.paddingLeft = 1;
    MARGIN_CHART_CONTAINER.paddingRight = -5;

    // Set data.
    let data = dataSet.datasets.map((item, key) => (
      {
        ...item,
        index: key,
        end: getTimeZoneFromUtc(item.end, timeZone),
        start: getTimeZoneFromUtc(item.start, timeZone),
        categoryLevel: item.elementId + "/" + item.UnitOfLearnings,
      }
    ));

    // Sort data.
    data = orderBy(data, [
      "elementId",
      "UnitOfLearnings",
    ], ["desc", "desc"]);

    // Get data Range.
    const dataTempRange = groupBy(data, "elementId");
    const dataRange = Object.keys(dataTempRange).map((item, key) => {
      const temp = orderBy(dataTempRange[item], [
        "elementId",
        "avgLevelOfCompetence",
      ], ["desc", "desc"]);
      return {
        startRangeModule: temp[0],
        endRangeModule: temp[temp.length - 1],
      };
    });

    // Set root instance.
    let root = am5.Root.new(chartID);
    root.dateFormatter.setAll({
      dateFormat: "dd/MM/yyyy",
      dateFields: ["valueX", "openValueX"],
    });

    // Set themes.
    root.setThemes([am5themes_Responsive.new(root)]);

    // Create chart
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "panY",
        behavior: "zoomX",
        maxTooltipDistance: 0,
        layout: root.verticalLayout,
        ...MARGIN_CHART_CONTAINER,
      })
    );

    // Add titles to chart
    chart.plotContainer.children.push(
      am5.Label.new(root, {
        text: `[bold]${elementName}[/]`,
        oversizedBehavior: "wrap",
        fontSize: 16,
        textAlign: "center",
        fontVariant: "small-caps",
        fontWeight: "100",
        x: -300,
        centerX: am5.percent(50),
        y: am5.percent(0),
        centery: am5.percent(0),
        paddingTop: -25,
        paddingBottom: 0,
      })
    );
    chart.plotContainer.children.push(
      am5.Label.new(root, {
        text: `[bold]Unit Of Learning[/]`,
        oversizedBehavior: "wrap",
        fontSize: 16,
        textAlign: "center",
        fontVariant: "small-caps",
        fontWeight: "100",
        x: -135,
        centerX: am5.percent(50),
        y: am5.percent(0),
        centery: am5.percent(0),
        paddingTop: -25,
        paddingBottom: 0,
      })
    );

    // Dissable scrollbar.
    chart.set(
      "scrollbarX",
      am5.Scrollbar.new(root, {
        orientation: "horizontal",
        visible: true,
      })
    );
    chart.set(
      "scrollbarY",
      am5.Scrollbar.new(root, {
        orientation: "vertical",
        visible: true,
        start: 0,
        end: getScrolling(data.length),
      })
    );

    // Always show.
    chart.zoomOutButton.set("forceHidden", false);

    // Remove logo.
    root._logo.dispose();

    // Create axes.
    let yAxisDiscipline = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "categoryLevel",
        fixAxisSize: true,
        maxDeviation: 0,
        tooltip: am5.Tooltip.new(root, {}),
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: MIN_GRID_VALUE,
        }),
      })
    );

    yAxisDiscipline.get("renderer").labels.template.setAll({
      oversizedBehavior: "wrap",
      maxWidth: 150,
      width: 150,
      textAlign: "left",
      fontSize: 12,
      fontWeight: "normal",
      paddingRight: 5,
      centerX: 0,
    });

    yAxisDiscipline.get("renderer").labels.template.adapters.add("text", (text, target) => {

      if (text === "{category}") {
        return "";
      };
      return text;

    });

    yAxisDiscipline.data.setAll(data);

    // Create axes.
    let yAxisUnitOfLearnings = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "_id",
        tooltip: am5.Tooltip.new(root, {}),
        renderer: am5xy.AxisRendererY.new(root, {
          minGridDistance: MIN_GRID_VALUE,
        }),
      })
    );

    yAxisUnitOfLearnings.get("renderer").labels.template.setAll({
      oversizedBehavior: "wrap",
      maxWidth: 200,
      width: 200,
      textAlign: "left",
      fontSize: 12,
      fontWeight: "normal",
    });

    yAxisUnitOfLearnings.get("renderer").labels.template.adapters.add("text", (value, target) => {
      const dataItem = target.dataItem;
      if (!!dataItem) {
        const label = find(data, { '_id': dataItem?.dataContext?._id });
        value = isObject(label) ? label?.UnitOfLearnings : value;
      };
      return value;
    });

    yAxisUnitOfLearnings.data.setAll(data);

    // Create axes.
    let xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: {
          timeUnit: "day",
          count: 1,
          
        },
        maxDeviation: 0.2,
        markUnitChange: false,
        strictMinMax: true,
        extraMax: 0.1,
        extraMin: 0.1,
        renderer: am5xy.AxisRendererX.new(root, {}),
      })
    );

    // Set Format instance.
    xAxis.get("dateFormats")["day"] = "dd-MMM-yyyy";
    xAxis.get("dateFormats")["month"] = "dd-MMM-yyyy";
    xAxis.get("periodChangeDateFormats")["month"] = "MMM-yyyy";

    // Remove Y grid line.
    xAxis.get("renderer").grid.template.set("forceHidden", true);

    xAxis.get("renderer").labels.template.setAll({
      oversizedBehavior: "fit",
      maxWidth: 100,
      paddingTop: 5,
      textAlign: "center",
      fontSize: 12,
      fontWeight: "normal",
    });

    // Remove Y grid line.
    yAxisDiscipline.get("renderer").grid.template.set("forceHidden", true);

    dataRange.forEach((item, idx, array) => {

      let range = yAxisDiscipline.makeDataItem({});
      yAxisDiscipline.createAxisRange(range);

      range.set("category", item.startRangeModule.categoryLevel);
      range.set("endCategory", item.endRangeModule.categoryLevel);

      let label = range.get("label");

      label.setAll({
        text: item.endRangeModule.elementId,
      });

      let tick = range.get("tick");
      tick.setAll({ 
        visible: true, 
        strokeOpacity: 0.3, 
        strokeWidth: 2,
        length: `-${viewport.width -30}`, 
        location: 0, 
      });

      // add range for the last grid.
      if (idx === array.length - 1) {
        let lastRange = yAxisDiscipline.makeDataItem({});
        yAxisDiscipline.createAxisRange(lastRange);
        lastRange.set("category", item.endRangeModule.categoryLevel);
        let lastTick = lastRange.get("tick");
        lastTick.setAll({ 
          visible: true, 
          strokeOpacity: 0.3, 
          strokeWidth: 2,
          length: `-${viewport.width -30}`, 
          location: 1, 
        });
        let lastLabel = lastRange.get("label");
        lastLabel.setAll({
          visible: false,
        });
      };

    });

    // Add series
    let series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        xAxis: xAxis,
        yAxis: yAxisUnitOfLearnings,
        openValueXField: "start",
        valueXField: "end",
        categoryYField: "_id",
        sequencedInterpolation: true,
      })
    );

    series.columns.template.setAll({
      templateField: "columnSettings",
      strokeOpacity: 0,
      tooltipText:
        "[bold]Unit Of Learning:[/] {UnitOfLearnings}. \n[bold]Discipline:[/] {elementId}. \n[bold]Start:[/] {start.formatDate('dd-MMM-yyyy')}. \n[bold]End:[/] {end.formatDate('dd-MMM-yyyy')}. \n[bold]Duration:[/] {duration}.",
    });

    series.data.processor = am5.DataProcessor.new(root, {
      dateFields: ["start", "end"],
      dateFormat: "dd/MM/yyyy",
    });

    series.bullets.push(() => {
      return am5.Bullet.new(root, {
        locationX: 1,
        locationY: 0.5,
        sprite: am5.Label.new(root, {
          centerY: am5.p50,
          text:
            "[bold]Start:[/] {start.formatDate('dd-MMM-yyyy')}. \n[bold]End:[/] {end.formatDate('dd-MMM-yyyy')}. \n[bold]Duration:[/] {duration}.",
          populateText: true,
          fontSize: 10,
          fontWeight: "normal",
        }),
      });
    });

    // SET VIRTUAL COLUMNS TO CURRENT DATE.
    if (data.length > 0) {
      let rangeDataItem = xAxis.makeDataItem({
        value: getTime(new Date()),
        endValue: getTime(new Date()),
      });

      series.createAxisRange(rangeDataItem);

      rangeDataItem.get("grid").setAll({
        visible: true,
      });

      rangeDataItem.get("axisFill").setAll({
        stroke: am5.color(0x000000),
        fill: am5.color(0x000000),
        strokeOpacity: 0.3,
        visible: true,
        strokeDasharray: [5, 5],
        strokeWidth: 2,
        fillWidth: 1,
        tooltipText: `[bold]Today:[/] ${format(new Date(), "dd-MMM-yyyy")}`,
        tooltip: am5.Tooltip.new(root, {}),
      });

      rangeDataItem.set(
        "bullet",
        am5xy.AxisBullet.new(root, {
          locationX: 1,
          locationY: 0.5,
          sprite: am5.Label.new(root, {
            centerX: am5.percent(5),
            centerY: am5.percent(80),
            text: `->[bold]Today: ${format(new Date(), "dd-MMM-yyyy")}[/].`,
            populateText: true,
            fontSize: 10,
            fontWeight: "normal",
          }),
        })
      );
    };

    series.data.setAll(data);

    // CREATE MODAL NOT DATA NOTE.
    let modal = am5.Modal.new(root, {
      content: "<span style='font-size: xx-large;'>The chart has no data!</span>",
    });

    series.events.on("datavalidated", (ev) => {
      let target = ev.target;
      if (target.data.length === 0) {
        // DISABLE LABELS.
        target.chart.plotContainer.children.values.forEach(item => item.className !== "Container" && item.set("forceHidden", true) )
        // SHOW MODAL.
        modal.open();
        return;
      };
      // CLOSED MODAL.
      modal.close();
    });

    // LOAD CHART.
    series.appear();
    chart.appear(1000, 100);

    // Return before dom render.
    return () => root.dispose();
  }, [chartID, dataSet, timeZone]);

  return (
    <div id={chartID} className="gantt-chart"></div>
  );
};
// Add PropTypes validation.
Gantt.propTypes = {
  chartID: PropTypes.string.isRequired,
  dataSet: PropTypes.object.isRequired,
};

export default React.memo(Gantt);
